import { CreativeFormatPlaybackTime, Option, PricingTableCell } from '../../../../generated/graphql';
import generateTempId from '../../../../utility/generateTempId';
import { EditingProjectState } from '../../../types/EditingProjectState';
import { InputtedCreationSet } from '../../../types/InputtedCreationSet';
import { InputtedCreativeFormat } from '../../../types/InputtedCreativeFormat';
import { InputtingCreativeFormat } from '../../../types/InputtingCreativeFormat';
import { InputtingCreativeFormatsFilter } from '../../../types/InputtingCreativeFormatsFilter';

const filteredInputtingCreativeFormats = ({
  inputtingCreativeFormats,
  inputtingCreativeFormatsFilter,
}: {
  inputtingCreativeFormats: ReadonlyArray<InputtingCreativeFormat>;
  inputtingCreativeFormatsFilter: InputtingCreativeFormatsFilter | null;
}): ReadonlyArray<InputtingCreativeFormat> => {
  // Update the creativeFormats array based on the given inputtingCreativeFormatsFilter
  const getFilteredInputtingCreativeFormats = (
    _inputtingCreativeFormats: ReadonlyArray<InputtingCreativeFormat>,
    _inputtingCreativeFormatsFilter: InputtingCreativeFormatsFilter,
  ) => {
    let filteredInputtingCreativeFormats = _inputtingCreativeFormats;

    if (_inputtingCreativeFormatsFilter.networksAndPlacements.some((item) => item.selected)) {
      filteredInputtingCreativeFormats = filteredInputtingCreativeFormats.filter((inputtingCreativeFormat) => {
        return _inputtingCreativeFormatsFilter.networksAndPlacements.find((networkAndPlacement) => {
          return (
            inputtingCreativeFormat.placement.id === networkAndPlacement.placement.id && networkAndPlacement.selected
          );
        });
      });
    }

    if (_inputtingCreativeFormatsFilter.creativeTypes.some((item) => item.selected)) {
      filteredInputtingCreativeFormats = filteredInputtingCreativeFormats.filter((inputtingCreativeFormat) => {
        return _inputtingCreativeFormatsFilter.creativeTypes.find((creativeType) => {
          return creativeType.creativeType.id === inputtingCreativeFormat.creativeType.id && creativeType.selected;
        });
      });
    }

    return filteredInputtingCreativeFormats;
  };

  if (inputtingCreativeFormatsFilter === null) return inputtingCreativeFormats;

  const filteredInputtingCreativeFormats = getFilteredInputtingCreativeFormats(
    inputtingCreativeFormats,
    inputtingCreativeFormatsFilter,
  );

  return inputtingCreativeFormats.map((item) => ({
    ...item,
    show: !!filteredInputtingCreativeFormats.find((filteredItem) => filteredItem === item),
  }));
};

const createInputtedCreativeFormat = ({
  inputtingCreativeFormat,
  playbackTime,
  selectedPricingMerchandiseOptions,
  requiredPointCell,
}: {
  inputtingCreativeFormat: Readonly<InputtingCreativeFormat>;
  playbackTime: Readonly<Omit<CreativeFormatPlaybackTime, 'id'>>;
  selectedPricingMerchandiseOptions: ReadonlyArray<Option>;
  requiredPointCell?: Readonly<PricingTableCell>;
}): InputtedCreativeFormat =>
  (({
    tmpid: generateTempId(),
    resizeBaseId: inputtingCreativeFormat.resizeBaseId,
    network: {
      id: inputtingCreativeFormat.network.id,
      key: inputtingCreativeFormat.network.key,
      name: inputtingCreativeFormat.network.name,
    },
    placement: {
      id: inputtingCreativeFormat.placement.id,
      key: inputtingCreativeFormat.placement.key,
      name: inputtingCreativeFormat.placement.name,
      basicMerchandises: inputtingCreativeFormat.placement.basicMerchandises,
    },
    creativeType: {
      id: inputtingCreativeFormat.creativeType.id,
      key: inputtingCreativeFormat.creativeType.key,
      name: inputtingCreativeFormat.creativeType.name,
      requiredPoints: requiredPointCell ? requiredPointCell.prices : undefined,
    },
    attribute: {
      aspectRatio: inputtingCreativeFormat.aspectRatioItem
        ? {
            supportingAspectRatioId: inputtingCreativeFormat.aspectRatioId,
            requiredOption: inputtingCreativeFormat.aspectRatioItem.requiredOption,
            aspectRatio: inputtingCreativeFormat.aspectRatioItem.aspectRatio,
            size: inputtingCreativeFormat.aspectRatioItem.presetSize,
          }
        : undefined,
      playbackTime: playbackTime
        ? {
            supportingPlaybackTimeId: playbackTime.supportingPlaybackTimeId,
            requiredOption: playbackTime.requiredOption,
            seconds: playbackTime.seconds,
          }
        : undefined,
    },
    selectedPricingMerchandiseOptions: selectedPricingMerchandiseOptions,
  } as unknown) as InputtedCreativeFormat);

const createInputtedCreativeFormatWithoutPlaybackTime = ({
  inputtingCreativeFormat,
  selectedPricingMerchandiseOptions,
  requiredPointCell,
}: {
  inputtingCreativeFormat: Readonly<InputtingCreativeFormat>;
  selectedPricingMerchandiseOptions: ReadonlyArray<Option>;
  requiredPointCell?: Readonly<PricingTableCell>;
}): InputtedCreativeFormat =>
  (({
    tmpid: generateTempId(),
    resizeBaseId: inputtingCreativeFormat.resizeBaseId,
    network: {
      id: inputtingCreativeFormat.network.id,
      key: inputtingCreativeFormat.network.key,
      name: inputtingCreativeFormat.network.name,
    },
    placement: {
      id: inputtingCreativeFormat.placement.id,
      key: inputtingCreativeFormat.placement.key,
      name: inputtingCreativeFormat.placement.name,
      basicMerchandises: inputtingCreativeFormat.placement.basicMerchandises,
    },
    creativeType: {
      id: inputtingCreativeFormat.creativeType.id,
      key: inputtingCreativeFormat.creativeType.key,
      name: inputtingCreativeFormat.creativeType.name,
      requiredPoints: requiredPointCell ? requiredPointCell.prices : undefined,
    },
    attribute: {
      aspectRatio: inputtingCreativeFormat.aspectRatioItem
        ? {
            supportingAspectRatioId: inputtingCreativeFormat.aspectRatioId,
            requiredOption: inputtingCreativeFormat.aspectRatioItem.requiredOption,
            aspectRatio: inputtingCreativeFormat.aspectRatioItem.aspectRatio,
            size: inputtingCreativeFormat.aspectRatioItem.presetSize,
          }
        : undefined,
      playbackTime: undefined,
    },
    selectedPricingMerchandiseOptions: selectedPricingMerchandiseOptions,
  } as unknown) as InputtedCreativeFormat);

const getAddingInputtedCreativeFormats = ({
  inputtingCreativeFormats,
  firstCreationSet,
}: {
  inputtingCreativeFormats: ReadonlyArray<InputtingCreativeFormat>;
  firstCreationSet: InputtedCreationSet;
}): Array<InputtedCreativeFormat> => {
  const selectedInputtingCreativeFormats = inputtingCreativeFormats.filter(
    (inputtingCreativeFormat) => inputtingCreativeFormat.selected,
  );

  // 既にオプションが選択済みの場合(オプション選択フォームに一度行って戻ってきた等)、
  // 既に選択されているオプション情報を既存のcreativeFormatからコピーする
  const firstCreativeFormat = firstCreationSet ? firstCreationSet.creativeFormats[0] : null;
  const selectedPricingMerchandiseOptions = firstCreativeFormat
    ? firstCreativeFormat.selectedPricingMerchandiseOptions
    : [];

  const addingInputtedCreativeFormats: Array<InputtedCreativeFormat> = [];

  selectedInputtingCreativeFormats.forEach((inputtingCreativeFormat) => {
    if (inputtingCreativeFormat.selectedPlaybackTimes.length > 0) {
      inputtingCreativeFormat.selectedPlaybackTimes.forEach((playbackTime) => {
        const pricingTable = (inputtingCreativeFormat.placement.pricingTables || []).find(
          (table) => table.creativeTypeId == inputtingCreativeFormat.creativeType.id,
        );

        const requiredPointCell = pricingTable
          ? pricingTable.cells.find((cell) => {
              const aspectRatioKey = `${inputtingCreativeFormat.aspectRatioItem!.presetSize.width}x${
                inputtingCreativeFormat.aspectRatioItem!.presetSize.height
              }`;
              return (
                cell.supportingAspectRatioKey === aspectRatioKey &&
                cell.supportingPlaybackTimeLength === playbackTime.seconds.toString()
              );
            })
          : undefined;

        addingInputtedCreativeFormats.push(
          createInputtedCreativeFormat({
            inputtingCreativeFormat,
            playbackTime,
            selectedPricingMerchandiseOptions,
            requiredPointCell,
          }),
        );
      });
    } else {
      const pricingTable = (inputtingCreativeFormat.placement.pricingTables || []).find(
        (table) => table.creativeTypeId == inputtingCreativeFormat.creativeType.id,
      );
      const requiredPointCell = pricingTable
        ? pricingTable.cells.find((cell) => {
            const aspectRatioKey = `${inputtingCreativeFormat.aspectRatioItem!.presetSize.width}x${
              inputtingCreativeFormat.aspectRatioItem!.presetSize.height
            }`;
            return cell.supportingAspectRatioKey == aspectRatioKey;
          })
        : undefined;
      addingInputtedCreativeFormats.push(
        createInputtedCreativeFormatWithoutPlaybackTime({
          inputtingCreativeFormat,
          requiredPointCell,
          selectedPricingMerchandiseOptions,
        }),
      );
    }
  });

  return addingInputtedCreativeFormats;
};

export const inputtedCreativeFormatsConverter = (state: EditingProjectState) => ({
  filter: () => filteredInputtingCreativeFormats(state),
  adding: () =>
    getAddingInputtedCreativeFormats({
      inputtingCreativeFormats: state.inputtingCreativeFormats,
      firstCreationSet: state.creationSet!,
    }),
});
